/**
 * RTL (Right-to-Left) Utilities
 * Helper functions for handling RTL layout in Arabic interface
 */

/**
 * Apply RTL or LTR direction to document
 * @param {string} language - Language code ('ar' or 'en')
 */
export const applyDirection = (language) => {
  const isRTL = language === 'ar';
  document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
  document.documentElement.lang = language;
  
  // Store in localStorage for persistence
  localStorage.setItem('direction', isRTL ? 'rtl' : 'ltr');
  localStorage.setItem('language', language);
};

/**
 * Get current direction
 * @returns {string} - 'rtl' or 'ltr'
 */
export const getDirection = () => {
  return document.documentElement.dir || localStorage.getItem('direction') || 'ltr';
};

/**
 * Check if current direction is RTL
 * @returns {boolean} - true if RTL, false if LTR
 */
export const isRTL = () => {
  return getDirection() === 'rtl';
};

/**
 * Get margin/padding property based on direction
 * Converts logical properties (start/end) to physical properties (left/right)
 * @param {string} property - Property like 'marginStart', 'paddingEnd'
 * @returns {string} - Physical property like 'marginLeft', 'paddingRight'
 */
export const getDirectionProperty = (property) => {
  const rtl = isRTL();
  
  const propertyMap = {
    'marginStart': rtl ? 'marginRight' : 'marginLeft',
    'marginEnd': rtl ? 'marginLeft' : 'marginRight',
    'paddingStart': rtl ? 'paddingRight' : 'paddingLeft',
    'paddingEnd': rtl ? 'paddingLeft' : 'paddingRight',
    'left': rtl ? 'right' : 'left',
    'right': rtl ? 'left' : 'right',
    'borderLeft': rtl ? 'borderRight' : 'borderLeft',
    'borderRight': rtl ? 'borderLeft' : 'borderRight'
  };
  
  return propertyMap[property] || property;
};

/**
 * Get RTL-aware positioning value
 * Inverts left/right values for RTL
 * @param {string} position - Position value like 'left', 'right', 'center'
 * @returns {string} - Adjusted position value
 */
export const getDirectionPosition = (position) => {
  if (!isRTL()) return position;
  
  const positionMap = {
    'left': 'right',
    'right': 'left',
    'flex-start': 'flex-end',
    'flex-end': 'flex-start'
  };
  
  return positionMap[position] || position;
};

/**
 * Get RTL-aware transform value
 * Inverts translateX and scaleX for RTL
 * @param {string} transform - Transform value
 * @returns {string} - Adjusted transform value
 */
export const getDirectionTransform = (transform) => {
  if (!isRTL()) return transform;
  
  // Invert translateX values
  return transform.replace(/translateX\((-?\d+(?:\.\d+)?(?:px|%|rem|em)?)\)/g, (match, value) => {
    const numValue = parseFloat(value);
    const unit = value.replace(/[-\d.]/g, '');
    return `translateX(${-numValue}${unit})`;
  });
};

/**
 * Get RTL-aware flexbox direction
 * @param {string} direction - Flex direction ('row', 'row-reverse', etc.)
 * @returns {string} - Adjusted flex direction
 */
export const getFlexDirection = (direction) => {
  if (!isRTL()) return direction;
  
  const directionMap = {
    'row': 'row-reverse',
    'row-reverse': 'row'
  };
  
  return directionMap[direction] || direction;
};

/**
 * Get RTL-aware icon rotation
 * Some icons need to be flipped for RTL (arrows, chevrons)
 * @param {boolean} shouldFlip - Whether the icon should flip
 * @returns {object} - Style object with transform
 */
export const getIconFlip = (shouldFlip = true) => {
  if (!isRTL() || !shouldFlip) return {};
  return { transform: 'scaleX(-1)' };
};

/**
 * Initialize RTL support on app load
 * Reads from localStorage and applies direction
 */
export const initializeDirection = () => {
  const savedLanguage = localStorage.getItem('language') || 'en';
  applyDirection(savedLanguage);
};

export default {
  applyDirection,
  getDirection,
  isRTL,
  getDirectionProperty,
  getDirectionPosition,
  getDirectionTransform,
  getFlexDirection,
  getIconFlip,
  initializeDirection
};
